// Considered: AddTemplate + AddSphere two-dialog handoff — rejected because this spec requires a single
// dialog instance across both Choose and Configure steps with unified Save-as-draft / Create footer.
// See spec Reuse Audit (specs/add-sphere-overhaul.md).
import React, { useState, useCallback, useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'localization';
import { useApiClientsOrNull } from '../../../context/ApiClientsContext';
import { useAnalytics } from '@center-technologies/analytics';
import { useAvailableTypes } from '../../../hooks/useAvailableTypes';
import { useUseCases } from '../../../hooks/useUseCases';
import { useTemplateApproaches } from '../../../hooks/useTemplateApproaches';
import CustomDialogAtom from '../../atoms/CustomDialog/CustomDialogAtom';
import SegmentedControlAtom from '../../atoms/SegmentedControl/SegmentedControlAtom';
import AddSphereChooseStepMolecule from '../../molecules/AddSphereChooseStep/AddSphereChooseStepMolecule';
import AddSphereConfigureStepMolecule from '../../molecules/AddSphereConfigureStep/AddSphereConfigureStepMolecule';
import DeployApproachStepMolecule from '../../molecules/DeployApproachStep/DeployApproachStepMolecule';
import type { Schema } from 'types/schema';
import type { Sphere } from 'types/global';
import type { TemplateApproach } from '../../../types/templateApproach';
import './AddSphereDialogOrganism.scss';

export interface AddSphereDialogOrganismProps {
  visible: boolean;
  onHide: () => void;
  parentSphere: Sphere;
  /** Open the dialog directly on a specific tab in the Choose step. */
  initialTab?: 'blank' | 'template';
  /**
   * Pre-selected schema (e.g. a "Start blank" type chip in the Atlas empty state).
   * When set, the dialog opens straight on the Configure step for this type, skipping
   * the Choose step (the type glyph's Back still returns to type selection).
   */
  initialSchema?: Schema | null;
  onSphereCreated: (sphere: Sphere) => void;
  /** Open the dialog in Deploy mode (Create or Deploy pill). Default: 'create'. */
  initialMode?: 'create' | 'deploy';
  /** Pre-targeted sphere when opening in Deploy mode from row-menu "Deploy approach". */
  targetSphere?: Sphere;
  /**
   * Opens a sphere the user just deployed an approach onto. The dialog closes first. When
   * omitted, the Deploy step's completed state offers *Done* only.
   */
  onOpenSphere?: (sphereId: string) => void;
}

type Step = 'choose' | 'configure';
type DialogMode = 'create' | 'deploy';

const AddSphereDialogOrganism: React.FC<AddSphereDialogOrganismProps> = ({
  visible,
  onHide,
  parentSphere,
  initialTab = 'blank',
  initialSchema = null,
  onSphereCreated,
  initialMode = 'create',
  targetSphere,
  onOpenSphere,
}) => {
  const { t } = useTranslation();
  const apiClients = useApiClientsOrNull();
  const logAnalyticsEvent = useAnalytics();


  const [mode, setMode] = useState<DialogMode>(initialMode);
  const [step, setStep] = useState<Step>('choose');
  const [tab, setTab] = useState<'blank' | 'template'>(initialTab);
  const [selectedSchema, setSelectedSchema] = useState<Schema | null>(null);
  const [selectedApproach, setSelectedApproach] = useState<TemplateApproach | null>(null);

  const { schemas, loading: schemasLoading } = useAvailableTypes(
    apiClients,
    parentSphere?.id,
  );

  const { useCases } = useUseCases();

  const {
    approaches,
    loading: approachesLoading,
  } = useTemplateApproaches();

  const handleSelectSchema = useCallback(
    (schema: Schema) => {
      setSelectedSchema(schema);
      setSelectedApproach(null);
      setStep('configure');
    },
    [],
  );

  // When opened with a pre-selected type (Atlas empty-state "Start blank" chip),
  // jump straight to Configure for that type. Apply only on the open transition so
  // the user can still navigate Back to the Choose step afterwards.
  const wasVisibleRef = useRef(false);
  useEffect(() => {
    if (visible && !wasVisibleRef.current && initialSchema) {
      setSelectedSchema(initialSchema);
      setSelectedApproach(null);
      setStep('configure');
    }
    wasVisibleRef.current = visible;
  }, [visible, initialSchema]);

  const handleSelectTemplate = useCallback(
    (template: TemplateApproach) => {
      // Resolve the schema for the template's target type
      const schema = schemas.find((s) => s.system_name === template.templateFor) ?? null;
      setSelectedSchema(schema);
      setSelectedApproach(template);
      logAnalyticsEvent('add_sphere_template_selected', undefined, {
        approachSystemName: template.system_name,
        schemaSystemName: template.templateFor,
        official: template.official,
      });
      setStep('configure');
    },
    [schemas, logAnalyticsEvent],
  );

  const handleBack = useCallback(() => {
    setSelectedSchema(null);
    setSelectedApproach(null);
    setStep('choose');
  }, []);

  const handleSphereCreated = useCallback(
    (sphere: Sphere) => {
      onSphereCreated(sphere);
    },
    [onSphereCreated],
  );

  const handleHide = useCallback(() => {
    setStep('choose');
    setSelectedSchema(null);
    setSelectedApproach(null);
    setTab(initialTab);
    setMode(initialMode);
    onHide();
  }, [onHide, initialTab, initialMode]);

  const handleOpenDeployedSphere = useMemo(
    () =>
      onOpenSphere
        ? (sphereId: string) => {
            handleHide();
            onOpenSphere(sphereId);
          }
        : undefined,
    [onOpenSphere, handleHide],
  );

  const handleModeChange = useCallback((value: string | string[]) => {
    const v = Array.isArray(value) ? value[0] : value;
    setMode(v as DialogMode);
    setStep('choose');
    setSelectedSchema(null);
    setSelectedApproach(null);
  }, []);

  const parentName = (parentSphere?.properties as any)?.name ?? parentSphere?.id ?? '';

  const dialogHeader = (
    <div className="add-sphere-dialog__header" data-testid="add-sphere-dialog-header">
      <div className="add-sphere-dialog__header-icon">
        <i className={mode === 'deploy' ? 'pi pi-share-alt' : 'pi pi-plus'} />
      </div>
      <div className="add-sphere-dialog__header-text">
        <div className="add-sphere-dialog__header-title">
          {mode === 'deploy'
            ? t('addSphere.dialog.modes.deploy')
            : t('addSphere.dialog.title')}
        </div>
        {mode === 'create' && (
          <div className="add-sphere-dialog__header-subtitle">
            {t('addSphere.dialog.subtitle.childOf')}{' '}
            {parentName && (
              <span className="add-sphere-dialog__parent-pill">
                <span className="add-sphere-dialog__parent-av" aria-hidden="true">
                  {parentName.charAt(0).toUpperCase()}
                </span>
                {parentName}
              </span>
            )}{' '}
            {'· '}
            {step === 'choose'
              ? t('addSphere.dialog.subtitle.step1')
              : t('addSphere.dialog.subtitle.step2')}
          </div>
        )}
      </div>
      {/* Mode pill — Create ↔ Deploy */}
      <SegmentedControlAtom
        options={[
          { value: 'create', label: t('addSphere.dialog.modes.create') },
          { value: 'deploy', label: t('addSphere.dialog.modes.deploy') },
        ]}
        value={mode}
        onChange={handleModeChange}
        mode="single"
        variant="pills"
        size="small"
        className="add-sphere-dialog__mode-pill"
        data-testid="dialog-mode-pill"
        ariaLabel={t('addSphere.dialog.modes.ariaLabel')}
      />
      {mode === 'create' && step === 'configure' && (
        <button
          type="button"
          className="add-sphere-dialog__back-btn"
          onClick={handleBack}
        >
          <i className="pi pi-arrow-left" />
          {t('common.actions.back')}
        </button>
      )}
    </div>
  );

  return (
    <CustomDialogAtom
      header={dialogHeader}
      visible={visible}
      onHide={handleHide}
      className="add-sphere-dialog"
      style={{ width: '1120px', maxWidth: '95vw', height: '730px' }}
      showCloseIcon={false}
    >
      <div
        className="add-sphere-dialog__content"
        data-testid="add-sphere-dialog-organism"
      >
        {mode === 'deploy' ? (
          <DeployApproachStepMolecule
            targetSphere={targetSphere ?? null}
            onChangeSphere={() => { /* sphere picker handled internally */ }}
            onDeployComplete={handleHide}
            onOpenSphere={handleOpenDeployedSphere}
            onClose={handleHide}
          />
        ) : step === 'choose' ? (
          <AddSphereChooseStepMolecule
            parentSphere={parentSphere}
            tab={tab}
            onTabChange={setTab}
            schemas={schemas}
            schemasLoading={schemasLoading}
            useCases={useCases}
            onSelectSchema={handleSelectSchema}
            onSelectTemplate={handleSelectTemplate}
            approaches={approaches}
            approachesLoading={approachesLoading}
            onClose={handleHide}
          />
        ) : (
          selectedSchema && (
            <AddSphereConfigureStepMolecule
              parentSphere={parentSphere}
              schema={selectedSchema}
              onBack={handleBack}
              onSphereCreated={handleSphereCreated}
              onClose={handleHide}
              selectedApproach={selectedApproach ?? undefined}
            />
          )
        )}
      </div>
    </CustomDialogAtom>
  );
};

export default AddSphereDialogOrganism;
